import {
    AmountFilterModel,
    TransactionJournalModel,
    TransactionProducts,
    TransactionsFilterModel,
    TransactionsModel,
} from './transactions.model';
import {makeAutoObservable} from 'mobx';
import AppStore from '../../app.store';
import axios, {GenericAbortSignal} from 'axios';
import {api} from '../../api/endpoints';
import {toast} from 'react-toastify';
import {defaultMonthDateRange} from "../../utils/dateUtils";
import {isConnectivityError, isOfflineServiceEnabled} from '../../utils/offline/offline.cache';
import {
    cacheTransactionsLookup,
    getCachedTransactionsLookupAsync,
    getOfflineTransactionsAsync,
} from './transactions.offline';
import {createRequestGuard, PAGE_LIST_LOADING_INITIAL} from '../../utils/requestGuard';

const mergeTransactionsById = (lists: TransactionsModel[][]) => {
    const map = new Map<number, TransactionsModel>();

    lists.forEach((list) => {
        list?.forEach((transaction) => {
            map.set(transaction.id, transaction);
        });
    });

    return Array.from(map.values()).sort((left, right) => right.id - left.id);
};

type TransactionsTableView = 'simple' | 'extended';

interface CachedTransactionsListResponse {
    transactions?: TransactionsModel[];
    pages?: number;
    total_quantity?: number;
    [key: string]: any;
}

const getTransactionsLookupCriteria = (
    type: TransactionsTableView,
    filters: TransactionsFilterModel
) => {
    const {
        pages: _pages,
        total_quantity: _totalQuantity,
        ...requestFilters
    } = filters;

    return {
        view: type,
        ...requestFilters,
    };
};

const withOfflineTransactions = async (
    data: CachedTransactionsListResponse,
    filters: TransactionsFilterModel
) => {
    const offlineTransactions = await getOfflineTransactionsAsync();

    if (!offlineTransactions.length) {
        return data;
    }

    const onlineTransactions = data.transactions ?? [];
    const transactions = mergeTransactionsById([
        onlineTransactions,
        offlineTransactions,
    ]);
    const onlineTotal = Number(data.total_quantity ?? onlineTransactions.length ?? 0);
    const totalQuantity = Math.max(
        onlineTotal + offlineTransactions.length,
        transactions.length
    );
    const pageLimit = Number(filters.page_limit || 25);

    return {
        ...data,
        transactions,
        pages: Math.max(data.pages ?? 1, Math.ceil(totalQuantity / pageLimit), 1),
        total_quantity: totalQuantity,
    };
};

const getOfflineTransactionsFallbackData = async (
    type: TransactionsTableView,
    filters: TransactionsFilterModel
): Promise<CachedTransactionsListResponse> => {
    const [cachedList, offlineTransactions] = await Promise.all([
        getCachedTransactionsLookupAsync<CachedTransactionsListResponse>(
            'transactions_list',
            getTransactionsLookupCriteria(type, filters)
        ),
        getOfflineTransactionsAsync(),
    ]);

    if (cachedList) {
        const mergedTransactions = mergeTransactionsById([
            cachedList.transactions ?? [],
            offlineTransactions,
        ]);

        return {
            ...cachedList,
            transactions: mergedTransactions,
            pages: cachedList.pages ?? 1,
            total_quantity: Number(cachedList.total_quantity ?? 0) + offlineTransactions.length,
        };
    }

    return {
        transactions: offlineTransactions,
        pages: 1,
        total_quantity: offlineTransactions.length,
    };
};

const transactionsFilterInitialState = {
    ...defaultMonthDateRange(),
    business_unit_id: 0,
    user_id: 0,
    product_type_id: 0,
    category_id: 0,
    brand_id: 0,
    product_id: 0,
    measure_unit_id: 0,
    tran_type_id: 0,
    status: 0,
    page: 1,
    page_limit: 25,
    pages: 0,
    order_by: 'id',
    order_descending: true,
    id: 0,
    search: '',
    amount_compare: '',
    total_quantity: 0,
};

const amountFilterInitialState: AmountFilterModel = {
    symbol: '>',
    value: 0,
}

class TransactionsStore {
    public transactions?: TransactionsModel[] = [];
    public transactionJournal?: TransactionJournalModel[] = [];
    public transactionsFilter = transactionsFilterInitialState;
    public transactionGoods?: TransactionProducts[] = [];
    public transactionServices?: TransactionProducts[] = [];
    public reason = '';
    public amountFilter = amountFilterInitialState;
    public installmentsLoading = false;

    public loading = PAGE_LIST_LOADING_INITIAL;
    public error = '';
    public message = '';
    public prodsLoading = false;
    private loadGuard = createRequestGuard();

    public transactionsInit = (signal: GenericAbortSignal) => {
        void this.transactionsReq(signal);
    };

    constructor() {
        makeAutoObservable(this);
    }

    setProdsLoading(loading: boolean): void {
        this.prodsLoading = loading;
    }

    setInstallmentsLoading(loading: boolean) {
        this.installmentsLoading = loading;
    }

    setTransactions(data: any) {
        const {transactions} = data;

        this.transactions = transactions;
    }

    setTransactionGoods(data?: any) {
        const {goods} = data;
        this.transactionGoods = goods;
    }

    setTransactionJournal(data: any) {
        const {journal} = data;
        this.transactionJournal = journal;
    }

    setTransactionServices(data?: any) {
        const {services} = data;
        this.transactionServices = services;
    }

    setTransactionFiltersTotalQuantity(totalQuantity: number) {
        this.transactionsFilter.total_quantity = totalQuantity;
    }

    public transactionsReq = async (signal?: GenericAbortSignal) => {
        const requestId = this.loadGuard.next();
        const {simpleTableView} = AppStore;
        const type: TransactionsTableView = simpleTableView ? 'simple' : 'extended';
        this.setLoading(true);
        try {
            const {getToken} = AppStore;

            const headers = {
                Authorization: 'Bearer ' + getToken()!,
            };
            const filterUrl = Object.keys(this.transactionsFilter).reduce(
                (filters, filter) => {
                    const symbol = filters ? '&' : '?';
                    const filterKeyName = filter as keyof TransactionsFilterModel;
                    // Исключаем total_quantity из параметров запроса
                    if (filterKeyName === 'total_quantity') {
                        return filters;
                    }
                    if (
                        this.transactionsFilter[filterKeyName] !== 0 &&
                        this.transactionsFilter[filterKeyName] !== ''
                    ) {
                        filters +=
                            symbol + filter + '=' + this.transactionsFilter[filterKeyName];
                    }

                    return filters;
                },
                ''
            );
            const lookupCriteria = getTransactionsLookupCriteria(type, this.transactionsFilter);

            if (type === 'simple') {
                const response = await axios.get(api.transactions + filterUrl, {
                    headers,
                    signal,
                });
                const dataWithOfflineTransactions = await withOfflineTransactions(
                    response.data,
                    this.transactionsFilter
                );

                this.setTransactions({transactions: dataWithOfflineTransactions.transactions ?? []});
                this.setTransactionsFilterPages(dataWithOfflineTransactions.pages ?? 1);
                this.setTransactionFiltersTotalQuantity(dataWithOfflineTransactions.total_quantity ?? 0);
                void cacheTransactionsLookup('transactions_list', lookupCriteria, response.data);
                return response;
            } else {
                const response = await axios.get(api.transactionsExtended + filterUrl, {
                    headers,
                    signal,
                });
                const dataWithOfflineTransactions = await withOfflineTransactions(
                    response.data,
                    this.transactionsFilter
                );

                this.setTransactions({transactions: dataWithOfflineTransactions.transactions ?? []});
                this.setTransactionsFilterPages(dataWithOfflineTransactions.pages ?? 1);
                this.setTransactionFiltersTotalQuantity(dataWithOfflineTransactions.total_quantity ?? 0);
                void cacheTransactionsLookup('transactions_list', lookupCriteria, response.data);
                return response;
            }
        } catch (err) {
            if (isOfflineServiceEnabled() && isConnectivityError(err)) {
                const fallbackData = await getOfflineTransactionsFallbackData(
                    type,
                    this.transactionsFilter
                );
                const offlineTransactions = fallbackData.transactions ?? [];

                this.setTransactions({transactions: offlineTransactions});
                this.setTransactionsFilterPages(fallbackData.pages ?? 1);
                this.setTransactionFiltersTotalQuantity(
                    fallbackData.total_quantity ?? offlineTransactions.length
                );

                return {
                    data: fallbackData,
                    offlineCached: true,
                } as any;
            }
        } finally {
            if (this.loadGuard.isLatest(requestId)) {
                this.setLoading(false);
            }
        }
    };

    public transactionProductsReq = async (transaction_id: number, signal?: AbortSignal) => {
        this.setProdsLoading(true);

        const {getToken} = AppStore;

        const headers = {
            Authorization: 'Bearer ' + getToken()!,
        };

        try {
            const response = await axios.get(
                api.transaction_products + `?tran_id=${transaction_id}`,
                {headers, signal}
            );

            this.setTransactionGoods(response.data);
            this.setTransactionServices(response.data);

            return response;
        } catch (err) {
        } finally {
            this.setProdsLoading(false);
        }
    };

    public setAmountFilter = ({symbol, value}: AmountFilterModel) => {
        const isUndefined = (value: any) => typeof value === 'undefined';
        this.amountFilter = {
            ...this.amountFilter,
            symbol: isUndefined(symbol) ? this.amountFilter.symbol! : symbol!,
            value: isUndefined(value) ? this.amountFilter.value! : value!,
        };
    }

    public transactionJournalReq = async (id: number) => {
        try {
            const {getToken} = AppStore;
            const headers = {
                Authorization: 'Bearer ' + getToken()!,
            };

            const response = await axios.get(
                api.transaction_journal + `?trans_id=${id}`,
                {headers}
            );

            this.setTransactionJournal(response.data);
        } catch (err) {
        }
    };

    public setTransactionsFilter = ({
                                        id,
                                        user_id,
                                        business_unit_id,
                                        brand_id,
                                        category_id,
                                        product_type_id,
                                        tran_type_id,
                                        date_from,
                                        date_to,
                                        status,
                                        search,
                                        amount_compare,
                                        page,
                                        page_limit,
                                    }: TransactionsFilterModel) => {
        const isUndefined = (value: any) => typeof value === 'undefined';
        this.transactionsFilter = {
            ...this.transactionsFilter,
            id: isUndefined(id) ? this.transactionsFilter.id : id!,
            user_id: isUndefined(user_id)
                ? this.transactionsFilter.user_id!
                : user_id!,
            business_unit_id: isUndefined(business_unit_id)
                ? this.transactionsFilter.business_unit_id!
                : business_unit_id!,
            brand_id: isUndefined(brand_id)
                ? this.transactionsFilter.brand_id!
                : brand_id!,
            category_id: isUndefined(category_id)
                ? this.transactionsFilter.category_id!
                : category_id!,
            product_type_id: isUndefined(product_type_id)
                ? this.transactionsFilter.product_type_id!
                : product_type_id!,
            tran_type_id: isUndefined(tran_type_id)
                ? this.transactionsFilter.tran_type_id!
                : tran_type_id!,
            date_from: isUndefined(date_from)
                ? this.transactionsFilter.date_from!
                : date_from!,
            date_to: isUndefined(date_to)
                ? this.transactionsFilter.date_to!
                : date_to!,
            status: isUndefined(status) ? this.transactionsFilter.status! : status!,
            search: isUndefined(search) ? this.transactionsFilter.search! : search!,
            amount_compare: isUndefined(amount_compare) ? this.transactionsFilter.amount_compare! : amount_compare!,
            page: isUndefined(page) ? this.transactionsFilter.page! : page!,
            page_limit: isUndefined(page_limit)
                ? this.transactionsFilter.page_limit!
                : page_limit!,
        };

        if (page_limit) {
            this.transactionsFilter.page = 1;
        }

        // Сбрасываем total_quantity при изменении фильтров
        this.transactionsFilter.total_quantity = 0;

        void this.transactionsReq();
    };

    setTransactionsFilterPages(pages: number) {
        this.transactionsFilter.pages = pages;
    }

    private setLoading(loadingState: boolean) {
        this.loading = loadingState;
    }

    public destroyProds = () => {
        this.transactionServices = [];
        this.transactionGoods = [];
    };

    public destroyJournal = () => {
        this.transactionJournal = [];
    };

    public getTransactionInstallment = async (transactionId: number) => {
        this.setInstallmentsLoading(true)

        const {getToken} = AppStore;
        const headers = {Authorization: 'Bearer ' + getToken()!};

        try {
            return await axios.get(api.transactions + `/installment/${transactionId}`, {headers})
        } catch (e: any) {
            toast.error(e?.response?.data.error)
        } finally {
            this.setInstallmentsLoading(false);
        }
    }

    public getInstallmentCommissionCheque = async (transactionId: number) => {
        const {getToken} = AppStore;
        const headers = {Authorization: 'Bearer ' + getToken()!};

        try {
            return await axios.get(api.installment_commission_cheque + `/${transactionId}`, {headers})
        } catch (e: any) {
            toast.error(e?.response?.data.error)
        } finally {
        }
    }
}

export default new TransactionsStore();
